/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";
import { API_ESA_OFFICE_UPLOAD } from "utils";

function UsersKhususLaporan() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tanggalAwal, setTanggalAwal] = useState("");
  const [tanggalAkhir, setTanggalAkhir] = useState("");
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Set default dates on component mount
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    setTanggalAwal(firstDay.toISOString().split("T")[0]);
    setTanggalAkhir(today.toISOString().split("T")[0]);

    // Auto-load data with default dates
    fetchData(
      firstDay.toISOString().split("T")[0],
      today.toISOString().split("T")[0]
    );
  }, []);

  const fetchData = async (tanggalAwal, tanggalAkhir) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}laporan-harian/users-khusus/laporan`,
        {
          params: {
            tanggal_awal: tanggalAwal,
            tanggal_akhir: tanggalAkhir,
          },
        }
      );

      if (response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data laporan harian users khusus",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetData = () => {
    if (!tanggalAwal || !tanggalAkhir) {
      swal({
        title: "Error",
        text: "Tanggal awal dan tanggal akhir harus diisi",
        icon: "error",
      });
      return;
    }

    if (new Date(tanggalAwal) > new Date(tanggalAkhir)) {
      swal({
        title: "Error",
        text: "Tanggal awal tidak boleh lebih besar dari tanggal akhir",
        icon: "error",
      });
      return;
    }

    fetchData(tanggalAwal, tanggalAkhir);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDetailClick = (pegawai) => {
    // Navigate to detail page with date range params (mirror rekapLaporanDinas.js)
    navigate(
      `/daily-performance/laporan-harian/perorang-detail/${
        pegawai.uid_RSS
      }?tanggal_awal=${tanggalAwal}&tanggal_akhir=${tanggalAkhir}&nama=${encodeURIComponent(
        pegawai.nama_pegawai || ""
      )}`
    );
  };

  const handleExportExcel = () => {
    if (!data || !data.data_pegawai) {
      swal({
        title: "Error",
        text: "Tidak ada data untuk diekspor",
        icon: "error",
      });
      return;
    }

    try {
      // Create HTML table
      let tableHTML = `
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Nama Pegawai</th>
              <th>Jabatan</th>
              <th>Dinas</th>
              <th>Bidang</th>
              <th>Pangkat</th>
              <th>Jumlah Laporan</th>
              <th>Total Kegiatan</th>
              <th>Rata-rata Kegiatan</th>
              <th>Persentase Kepatuhan</th>
            </tr>
          </thead>
          <tbody>
      `;

      data.data_pegawai.forEach((pegawai, index) => {
        tableHTML += `
          <tr>
            <td>${index + 1}</td>
            <td>${pegawai.nama_pegawai}</td>
            <td>${pegawai.nama_jabatan}</td>
            <td>${pegawai.nama_dinas}</td>
            <td>${pegawai.nama_bidang}</td>
            <td>${pegawai.pangkat}</td>
            <td>${pegawai.jumlah_laporan}</td>
            <td>${pegawai.total_kegiatan}</td>
            <td>${pegawai.rata_rata_kegiatan_per_laporan}</td>
            <td>${pegawai.persentase_kepatuhan_individual}%</td>
          </tr>
        `;
      });

      tableHTML += `
          </tbody>
        </table>
      `;

      // Convert to Excel
      const blob = new Blob([tableHTML], { type: "application/vnd.ms-excel" });
      const fileName = `Laporan_Harian_Users_Khusus_${tanggalAwal}_${tanggalAkhir}.xls`;
      saveAs(blob, fileName);

      swal({
        title: "Success",
        text: "Data berhasil diekspor ke Excel",
        icon: "success",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      swal({
        title: "Error",
        text: "Gagal mengekspor data",
        icon: "error",
      });
    }
  };

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "15%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "12%" },

    {
      Header: "Dinas",
      accessor: "nama_dinas",
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="medium"
          sx={{
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.4,
            width: "200px",
            maxWidth: "200px",
            minWidth: "200px",
            display: "block",
          }}
        >
          {value}
        </MDTypography>
      ),
    },
    { Header: "Pangkat", accessor: "pangkat", width: "8%" },
    { Header: "Jumlah Laporan", accessor: "jumlah_laporan", width: "8%" },
    { Header: "Total Kegiatan", accessor: "total_kegiatan", width: "8%" },
    {
      Header: "Rata-rata/Laporan",
      accessor: "rata_rata_kegiatan_per_laporan",
      width: "8%",
    },
    {
      Header: "Persentase Kepatuhan",
      accessor: "persentase_kepatuhan_individual",
      width: "12%",
      Cell: ({ value }) => (
        <Chip
          label={`${value}%`}
          color={
            parseFloat(value) >= 80
              ? "success"
              : parseFloat(value) >= 60
              ? "warning"
              : "error"
          }
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Detail",
      accessor: "detail",
      width: "8%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => handleDetailClick(row.original)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
        </MDButton>
      ),
    },
  ];

  const rows =
    data?.data_pegawai
      ?.sort((a, b) => {
        // Urutkan berdasarkan persentase kepatuhan dari tertinggi ke terendah
        const persentaseA = parseFloat(a.persentase_kepatuhan_individual) || 0;
        const persentaseB = parseFloat(b.persentase_kepatuhan_individual) || 0;
        return persentaseB - persentaseA; // Descending order (tertinggi ke terendah)
      })
      ?.map((pegawai, index) => ({
        no: index + 1,
        uid_RSS: pegawai.uid_RSS,
        nama_pegawai: pegawai.nama_pegawai,
        nama_jabatan: pegawai.nama_jabatan,
        nama_dinas: pegawai.nama_dinas,
        profil: pegawai.profil,
        nama_bidang: pegawai.nama_bidang || "-",
        pangkat: pegawai.pangkat,
        jumlah_laporan: pegawai.jumlah_laporan,
        total_kegiatan: pegawai.total_kegiatan,
        rata_rata_kegiatan_per_laporan: pegawai.rata_rata_kegiatan_per_laporan,
        persentase_kepatuhan_individual:
          pegawai.persentase_kepatuhan_individual,
      })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox mb={3}>
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    gap={2}
                  >
                    <MDTypography variant="h6" gutterBottom>
                      Statistik Laporan Harian Pimpinan
                    </MDTypography>
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={2}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      width={{ xs: "100%", md: "auto" }}
                    >
                      <MDInput
                        type="date"
                        label="Tanggal Awal"
                        value={tanggalAwal}
                        onChange={(e) => setTanggalAwal(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                      <MDInput
                        type="date"
                        label="Tanggal Akhir"
                        value={tanggalAkhir}
                        onChange={(e) => setTanggalAkhir(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={handleGetData}
                        disabled={loading}
                        fullWidth={isMobile}
                        sx={{
                          minWidth: isMobile ? "auto" : "120px",
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "Get Data"
                        )}
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>

                {data && (
                  <>
                    <MDTypography variant="button" color="text" gutterBottom>
                      Users Khusus (Pimpinan Daerah dan Perangkat Daerah) -{" "}
                      {new Date(tanggalAwal).toLocaleDateString("id-ID")} s/d{" "}
                      {new Date(tanggalAkhir).toLocaleDateString("id-ID")}
                    </MDTypography>

                    <Grid container spacing={3} mt={2}>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="info">
                            {data.statistik.total_pegawai}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Pegawai
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="success">
                            {data.statistik.total_laporan}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Laporan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="primary">
                            {data.statistik.total_kegiatan}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Kegiatan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="warning">
                            {data.statistik.rata_rata_kegiatan_per_laporan}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata/Laporan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="secondary">
                            {data.statistik.rata_rata_kegiatan_per_hari || 0}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata/Hari
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="dark">
                            {data.statistik.total_laporan_seharusnya || 0}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Target Laporan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Progress Bar */}
                    <MDBox mt={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <MDTypography variant="button" color="text">
                          Persentase Kepatuhan
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          color={
                            data.statistik.persentase_kepatuhan >= 80
                              ? "success"
                              : data.statistik.persentase_kepatuhan >= 60
                              ? "warning"
                              : "error"
                          }
                        >
                          {data.statistik.persentase_kepatuhan}%
                        </MDTypography>
                      </MDBox>
                      <MDProgress
                        variant="determinate"
                        value={data.statistik.persentase_kepatuhan}
                        color={
                          data.statistik.persentase_kepatuhan >= 80
                            ? "success"
                            : data.statistik.persentase_kepatuhan >= 60
                            ? "warning"
                            : "error"
                        }
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {data && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Pegawai:{" "}
                        {new Date(tanggalAwal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}{" "}
                        s/d{" "}
                        {new Date(tanggalAkhir).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {data.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        rows.map((row, index) => (
                          <Card key={`${row.uid_RSS || "row"}-${index}`}>
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${row.uid_RSS}.jpg`}
                                      alt={row.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {row.nama_pegawai}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {row.nama_jabatan}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {row.nama_dinas}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {row.nama_bidang} • {row.pangkat}
                                  </MDTypography>
                                  <MDBox
                                    mt={1}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={0.5}
                                  >
                                    <Chip
                                      label={`Laporan: ${row.jumlah_laporan}`}
                                      color="primary"
                                      size="small"
                                    />
                                    <Chip
                                      label={`Kegiatan: ${row.total_kegiatan}`}
                                      color="warning"
                                      size="small"
                                    />
                                    <Chip
                                      label={`Rata2: ${row.rata_rata_kegiatan_per_laporan}`}
                                      size="small"
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Persentase Kepatuhan
                                    </MDTypography>
                                    <MDBox mt={0.5}>
                                      <MDProgress
                                        variant="gradient"
                                        value={
                                          parseFloat(
                                            row.persentase_kepatuhan_individual
                                          ) || 0
                                        }
                                        color={
                                          (parseFloat(
                                            row.persentase_kepatuhan_individual
                                          ) || 0) >= 80
                                            ? "success"
                                            : (parseFloat(
                                                row.persentase_kepatuhan_individual
                                              ) || 0) >= 60
                                            ? "warning"
                                            : "error"
                                        }
                                        sx={{
                                          width: "100%",
                                          height: 8,
                                          borderRadius: 8,
                                        }}
                                      />
                                    </MDBox>
                                    <MDBox
                                      mt={0.5}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                    >
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {(
                                          parseFloat(
                                            row.persentase_kepatuhan_individual
                                          ) || 0
                                        ).toFixed(2)}
                                        %
                                      </MDTypography>
                                      <MDButton
                                        variant="gradient"
                                        color="info"
                                        size="small"
                                        onClick={() => handleDetailClick(row)}
                                        sx={{ minWidth: "auto", px: 1 }}
                                      >
                                        <Icon sx={{ fontSize: 16 }}>
                                          visibility
                                        </Icon>
                                        &nbsp;Detail
                                      </MDButton>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data laporan harian users khusus...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!data && !loading && tanggalAwal && tanggalAkhir && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data laporan harian users khusus
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih periode tanggal lain atau periksa kembali data
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersKhususLaporan;
